import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { TranslateModule } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AccountService } from '../core/account.service';
import { ApiService } from '../core/api.service';
import { AuthService } from '../core/auth.service';
import { UserDetails } from './admin/models';

interface PermGroup {
  system: string;
  resource: string;
  label: string;
  icon: string;
  actions: { full: string; verb: string; tone: string }[];
  expanded: boolean;
}

const VERB_TONE: Record<string, string> = {
  read: 'tone-info', view: 'tone-info', list: 'tone-info',
  write: 'tone-success', create: 'tone-success',
  update: 'tone-warn', edit: 'tone-warn',
  delete: 'tone-danger', remove: 'tone-danger',
  approve: 'tone-success', reject: 'tone-danger',
  'pre-approve': 'tone-success', 'pre-reject': 'tone-danger',
  accept: 'tone-success',
  invoice: 'tone-purple', transfer: 'tone-purple',
  deposit: 'tone-success', withdraw: 'tone-warn', collect: 'tone-success',
  send: 'tone-info', 'send-bulk': 'tone-info',
  print: 'tone-neutral', export: 'tone-neutral',
};

const RESOURCE_ICON: Record<string, string> = {
  '*': 'pi pi-star-fill',
  access: 'pi pi-key',
  users: 'pi pi-users',
  roles: 'pi pi-id-card',
  permissions: 'pi pi-shield',
  settings: 'pi pi-cog',
  reports: 'pi pi-chart-bar',
  patients: 'pi pi-user',
  samples: 'pi pi-flask',
  tests: 'pi pi-microchip',
  results: 'pi pi-check-square',
  orders: 'pi pi-shopping-cart',
  invoices: 'pi pi-file',
  payments: 'pi pi-credit-card',
  hr: 'pi pi-briefcase',
  employees: 'pi pi-user-edit',
  salaries: 'pi pi-dollar',
  inventory: 'pi pi-box',
  suppliers: 'pi pi-truck',
  customers: 'pi pi-users',
  audit: 'pi pi-history',
  logs: 'pi pi-list',
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    CardModule,
    InputTextModule,
    ButtonModule,
    TagModule,
    ChipModule,
    TabViewModule,
    ToastModule,
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    <div class="page-container">
      <div class="page-header">
        <h1 class="title">{{ 'PROFILE.TITLE' | translate }}</h1>
        <p class="sub">{{ 'PROFILE.SUBTITLE' | translate }}</p>
      </div>

      <div *ngIf="loading()" class="text-center p-4">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
        <p>{{ 'COMMON.LOADING' | translate }}</p>
      </div>

      <div *ngIf="!loading() && profile()" class="profile-content">
        <p-tabView>
          <!-- معلومات أساسية -->
          <p-tabPanel header="معلومات أساسية">
            <div class="card">
              <h3>معلومات الحساب</h3>
              <div class="form-grid">
                <div class="field">
                  <label>اسم المستخدم</label>
                  <input pInputText [value]="profile()!.username" disabled />
                  <small class="p-text-secondary">لا يمكن تعديل اسم المستخدم</small>
                </div>

                <div class="field">
                  <label>الاسم الأول</label>
                  <input pInputText [(ngModel)]="editForm.firstName" placeholder="الاسم الأول" />
                </div>

                <div class="field">
                  <label>اسم العائلة</label>
                  <input pInputText [(ngModel)]="editForm.lastName" placeholder="اسم العائلة" />
                </div>

                <div class="field">
                  <label>البريد الإلكتروني</label>
                  <input pInputText type="email" [(ngModel)]="editForm.email" placeholder="user@example.com" />
                </div>

                <div class="field">
                  <label>رقم الهاتف</label>
                  <input pInputText type="tel" [(ngModel)]="editForm.phone" placeholder="+1234567890" />
                </div>

                <div class="field">
                  <label>حالة الحساب</label>
                  <p-tag [severity]="profile()!.isActive ? 'success' : 'danger'"
                         [value]="profile()!.isActive ? 'نشط' : 'معطل'"></p-tag>
                </div>

                <div class="field" *ngIf="profile()!.isLocked">
                  <label>حالة القفل</label>
                  <p-tag severity="danger" value="مقفل"></p-tag>
                </div>
              </div>

              <div class="mt-4">
                <button pButton label="حفظ التغييرات" icon="pi pi-save"
                        (click)="saveProfile()" [loading]="saving()"></button>
              </div>
            </div>
          </p-tabPanel>

          <!-- الصلاحيات والأدوار -->
          <p-tabPanel header="الصلاحيات والأدوار">
            <!-- Roles -->
            <div class="card section-card">
              <div class="section-head">
                <div class="section-title">
                  <div class="section-icon icon-roles"><i class="pi pi-id-card"></i></div>
                  <h3>الأدوار</h3>
                </div>
                <span class="count-pill" *ngIf="profile()!.roles?.length">
                  {{ profile()!.roles!.length }}
                </span>
              </div>
              <div *ngIf="profile()!.roles && profile()!.roles!.length > 0" class="pill-list">
                <span class="role-pill" *ngFor="let role of profile()!.roles">
                  <i class="pi pi-shield"></i>
                  {{ role }}
                </span>
              </div>
              <div *ngIf="!profile()!.roles || profile()!.roles!.length === 0" class="empty-mini">
                <i class="pi pi-inbox"></i>
                لا توجد أدوار مخصصة
              </div>
            </div>

            <!-- Groups -->
            <div class="card section-card">
              <div class="section-head">
                <div class="section-title">
                  <div class="section-icon icon-groups"><i class="pi pi-users"></i></div>
                  <h3>المجموعات</h3>
                </div>
                <span class="count-pill" *ngIf="profile()!.groups?.length">
                  {{ profile()!.groups!.length }}
                </span>
              </div>
              <div *ngIf="profile()!.groups && profile()!.groups!.length > 0" class="pill-list">
                <span class="group-pill" *ngFor="let group of profile()!.groups">
                  <i class="pi pi-users"></i>
                  {{ group }}
                </span>
              </div>
              <div *ngIf="!profile()!.groups || profile()!.groups!.length === 0" class="empty-mini">
                <i class="pi pi-inbox"></i>
                لا توجد مجموعات
              </div>
            </div>

            <!-- Permissions -->
            <div class="card section-card perms-card">
              <div class="section-head">
                <div class="section-title">
                  <div class="section-icon icon-perms"><i class="pi pi-shield"></i></div>
                  <h3>الصلاحيات</h3>
                </div>
                <div class="perm-summary" *ngIf="permissions().length > 0">
                  <span><strong>{{ permissions().length }}</strong> صلاحية</span>
                  <span class="dot">·</span>
                  <span><strong>{{ systemsCount() }}</strong> نظام</span>
                  <span class="dot">·</span>
                  <span><strong>{{ permGroups().length }}</strong> مورد</span>
                </div>
              </div>

              <!-- Search + actions -->
              <div class="perm-toolbar" *ngIf="permissions().length > 0">
                <span class="p-input-icon-left search-wrap">
                  <i class="pi pi-search"></i>
                  <input
                    pInputText
                    [(ngModel)]="search"
                    (ngModelChange)="onSearchChange()"
                    placeholder="ابحث في الصلاحيات..."
                  />
                </span>
                <div class="perm-actions">
                  <button pButton class="p-button-text p-button-sm" icon="pi pi-angle-double-down"
                          label="فتح الكل" (click)="expandAll()"></button>
                  <button pButton class="p-button-text p-button-sm" icon="pi pi-angle-double-up"
                          label="طي الكل" (click)="collapseAll()"></button>
                </div>
              </div>

              <!-- Grouped permissions -->
              <div class="perm-groups" *ngIf="filteredGroups().length > 0">
                <div class="perm-group" *ngFor="let g of filteredGroups(); trackBy: trackGroup"
                     [class.is-open]="g.expanded">
                  <button class="pg-header" type="button" (click)="g.expanded = !g.expanded">
                    <div class="pg-icon"><i [class]="g.icon"></i></div>
                    <div class="pg-meta">
                      <span class="pg-name">{{ g.label }}</span>
                      <span class="pg-sys">{{ g.system }}</span>
                    </div>
                    <span class="pg-count">{{ g.actions.length }}</span>
                    <i class="pi chev"
                       [class.pi-chevron-up]="g.expanded"
                       [class.pi-chevron-down]="!g.expanded"></i>
                  </button>

                  <div class="pg-body" *ngIf="g.expanded">
                    <span class="verb-chip"
                          *ngFor="let a of g.actions"
                          [ngClass]="a.tone"
                          [title]="a.full">
                      {{ a.verb }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="empty-state" *ngIf="permissions().length === 0">
                <div class="empty-illust"><i class="pi pi-shield"></i></div>
                <h4>لا توجد صلاحيات</h4>
                <p>لم يتم تخصيص أي صلاحيات لحسابك حتى الآن.</p>
              </div>

              <div class="empty-state" *ngIf="permissions().length > 0 && filteredGroups().length === 0">
                <div class="empty-illust"><i class="pi pi-search"></i></div>
                <h4>لا نتائج</h4>
                <p>جرّب كلمة بحث مختلفة.</p>
              </div>
            </div>
          </p-tabPanel>

          <!-- معلومات إضافية -->
          <p-tabPanel header="معلومات إضافية">
            <div class="card">
              <h3>معلومات النظام</h3>
              <div class="info-grid">
                <div class="info-item">
                  <label>تاريخ الإنشاء:</label>
                  <span>{{ profile()!.createdAt | date:'medium' }}</span>
                </div>
                <div class="info-item">
                  <label>تاريخ آخر تحديث:</label>
                  <span>{{ profile()!.updatedAt | date:'medium' }}</span>
                </div>
                <div class="info-item" *ngIf="profile()!.lastLoginAt">
                  <label>آخر دخول:</label>
                  <span>{{ profile()!.lastLoginAt | date:'medium' }}</span>
                </div>
                <div class="info-item" *ngIf="profile()!.passwordChangedAt">
                  <label>تاريخ تغيير كلمة المرور:</label>
                  <span>{{ profile()!.passwordChangedAt | date:'medium' }}</span>
                </div>
                <div class="info-item">
                  <label>الجلسات النشطة:</label>
                  <span>{{ profile()!.sessionsCount || 0 }}</span>
                </div>
                <div class="info-item" *ngIf="profile()!.employeeId">
                  <label>رقم الموظف:</label>
                  <span>{{ profile()!.employeeId }}</span>
                </div>
              </div>
            </div>

            <div class="card mt-3" *ngIf="profile()!.mustChangePassword">
              <div class="p-3" style="background: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px;">
                <strong>تنبيه:</strong> يجب تغيير كلمة المرور عند الدخول التالي.
              </div>
            </div>
          </p-tabPanel>
        </p-tabView>
      </div>
    </div>
  `,
  styles: [`
    :host {
      --c-primary: #4f46e5;
      --c-primary-2: #7c3aed;
      --c-ink: #0f172a;
      --c-ink-soft: #334155;
      --c-muted: #64748b;
      --c-line: #e5e7eb;
      --c-line-soft: #eef0f4;
      --radius: 14px;
      --radius-sm: 10px;
    }
    .page-container { padding: 1.5rem; }
    .page-header { margin-bottom: 1.5rem; }
    .title { font-size: 2rem; font-weight: 700; margin: 0 0 0.5rem; color: var(--c-ink); letter-spacing: -0.01em; }
    .sub { color: var(--c-muted); margin: 0; }
    .profile-content { max-width: 1200px; }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 1.5rem;
      margin-top: 1rem;
    }
    .field { display: flex; flex-direction: column; gap: 0.5rem; }
    .field label { font-weight: 500; }

    .info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1rem;
      margin-top: 1rem;
    }
    .info-item { display: flex; flex-direction: column; gap: 0.25rem; }
    .info-item label { font-weight: 600; color: var(--c-muted); font-size: 0.875rem; }
    .info-item span { font-size: 1rem; }

    ::ng-deep .p-tabview .p-tabview-panels { padding: 1.5rem 0; display: flex; flex-direction: column; gap: 20px; }

    /* ===== Section card ===== */
    .section-card {
      border-radius: var(--radius);
      border: 1px solid var(--c-line-soft);
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
      padding: 22px 24px;
      background: #fff;
    }
    .section-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 18px;
      flex-wrap: wrap;
    }
    .section-title {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .section-title h3 { margin: 0; font-size: 17px; font-weight: 700; color: var(--c-ink); }
    .section-icon {
      width: 38px; height: 38px; border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      font-size: 15px;
      flex-shrink: 0;
    }
    .icon-roles  { background: #fef3c7; color: #b45309; }
    .icon-groups { background: #dbeafe; color: #1d4ed8; }
    .icon-perms  { background: linear-gradient(135deg, #eef2ff, #f3e8ff); color: var(--c-primary); }

    .count-pill {
      background: linear-gradient(135deg, #f1f5f9, #f8fafc);
      color: var(--c-ink-soft);
      border: 1px solid var(--c-line);
      padding: 4px 12px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 700;
    }

    .perm-summary {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: var(--c-muted);
    }
    .perm-summary strong { color: var(--c-ink); font-weight: 700; }
    .perm-summary .dot { opacity: 0.5; }

    /* ===== Role & group pills ===== */
    .pill-list {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
    .role-pill, .group-pill {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 7px 14px;
      border-radius: 9px;
      font-size: 13px;
      font-weight: 600;
      border: 1px solid;
    }
    .role-pill  { background: #fef3c7; color: #92400e; border-color: #fde68a; }
    .role-pill i  { color: #b45309; font-size: 11px; }
    .group-pill { background: #dbeafe; color: #1e40af; border-color: #bfdbfe; }
    .group-pill i { color: #1d4ed8; font-size: 11px; }

    .empty-mini {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--c-muted);
      font-size: 13px;
      padding: 10px 0;
    }
    .empty-mini i { font-size: 14px; opacity: 0.7; }

    /* ===== Toolbar (search + actions) ===== */
    .perm-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-bottom: 18px;
      flex-wrap: wrap;
    }
    .search-wrap { flex: 1; max-width: 380px; }
    .search-wrap input {
      width: 100%;
      padding-inline-start: 38px;
      height: 40px;
      border-radius: var(--radius-sm);
      border: 1px solid var(--c-line);
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    .search-wrap input:focus {
      border-color: var(--c-primary);
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.12);
      outline: none;
    }
    .perm-actions { display: flex; gap: 4px; }
    .perm-actions ::ng-deep .p-button-text { color: var(--c-muted); font-size: 13px; }
    .perm-actions ::ng-deep .p-button-text:hover { color: var(--c-primary); background: rgba(79, 70, 229, 0.06); }

    /* ===== Permission groups ===== */
    .perm-groups {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .perm-group {
      background: #fff;
      border: 1px solid var(--c-line-soft);
      border-radius: 12px;
      overflow: hidden;
      transition: box-shadow 0.2s, border-color 0.2s;
    }
    .perm-group:hover {
      border-color: #cfd3dc;
    }
    .perm-group.is-open {
      border-color: rgba(79, 70, 229, 0.4);
      box-shadow: 0 4px 14px -6px rgba(79, 70, 229, 0.18);
    }
    .pg-header {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 14px 18px;
      background: transparent;
      border: 0;
      cursor: pointer;
      text-align: start;
      transition: background 0.18s;
    }
    .pg-header:hover { background: #fafbfc; }
    .perm-group.is-open .pg-header {
      background: #fafbff;
      border-bottom: 1px solid var(--c-line-soft);
    }
    .pg-icon {
      width: 38px; height: 38px; border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      background: linear-gradient(135deg, #eef2ff, #f3e8ff);
      color: var(--c-primary);
      font-size: 15px;
      flex-shrink: 0;
    }
    .perm-group.is-open .pg-icon {
      background: linear-gradient(135deg, var(--c-primary), var(--c-primary-2));
      color: #fff;
    }
    .pg-meta {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }
    .pg-name { font-weight: 700; color: var(--c-ink); font-size: 15px; }
    .pg-sys {
      background: #eef2ff; color: #4338ca; border: 1px solid #e0e7ff;
      border-radius: 999px; padding: 2px 10px; font-size: 10.5px;
      font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    }
    .pg-count {
      min-width: 28px;
      padding: 3px 10px;
      border-radius: 999px;
      background: #f1f5f9;
      color: var(--c-ink-soft);
      font-size: 12px;
      font-weight: 700;
      text-align: center;
    }
    .perm-group.is-open .pg-count {
      background: rgba(79, 70, 229, 0.1);
      color: var(--c-primary);
    }
    .chev {
      width: 28px; height: 28px; border-radius: 7px;
      display: flex; align-items: center; justify-content: center;
      color: var(--c-muted);
      font-size: 12px;
    }
    .pg-header:hover .chev { color: var(--c-primary); }

    .pg-body {
      padding: 16px 20px 18px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      background: #fafbfc;
      animation: slide-down 0.22s ease;
    }

    /* ===== Verb chips ===== */
    .verb-chip {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 5px 13px;
      border-radius: 8px;
      font-size: 12.5px;
      font-weight: 700;
      font-family: 'JetBrains Mono', 'Courier New', monospace;
      letter-spacing: 0.02em;
      border: 1px solid transparent;
      cursor: default;
    }
    .verb-chip.tone-info    { background: #dbeafe; color: #1d4ed8; border-color: #bfdbfe; }
    .verb-chip.tone-success { background: #dcfce7; color: #166534; border-color: #bbf7d0; }
    .verb-chip.tone-warn    { background: #fef3c7; color: #92400e; border-color: #fde68a; }
    .verb-chip.tone-danger  { background: #fee2e2; color: #991b1b; border-color: #fecaca; }
    .verb-chip.tone-purple  { background: #f3e8ff; color: #6b21a8; border-color: #e9d5ff; }
    .verb-chip.tone-neutral { background: #f1f5f9; color: var(--c-ink-soft); border-color: #e2e8f0; }

    /* ===== Empty state ===== */
    .empty-state {
      padding: 48px 16px;
      text-align: center;
      color: var(--c-muted);
    }
    .empty-illust {
      width: 64px; height: 64px;
      margin: 0 auto 12px;
      border-radius: 50%;
      background: linear-gradient(135deg, #eef2ff, #f3e8ff);
      display: flex; align-items: center; justify-content: center;
    }
    .empty-illust i { font-size: 26px; color: var(--c-primary); }
    .empty-state h4 { margin: 0 0 4px; font-size: 15px; color: var(--c-ink); font-weight: 700; }
    .empty-state p { margin: 0; font-size: 13px; }

    @keyframes slide-down {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    @media (max-width: 700px) {
      .section-card { padding: 18px 16px; }
      .perm-summary { font-size: 12px; gap: 6px; }
      .search-wrap { max-width: 100%; }
      .pg-header { gap: 10px; padding: 12px 14px; }
      .pg-name { font-size: 14px; }
    }
  `]
})
export class ProfileComponent implements OnInit {
  accountService = inject(AccountService);
  apiService = inject(ApiService);
  authService = inject(AuthService);
  ms = inject(MessageService);

  profile = signal<UserDetails | null>(null);
  loading = signal(true);
  saving = signal(false);
  permissions = signal<string[]>([]);

  search = '';
  // bumped on every change so computed() re-evaluates
  private searchTick = signal(0);

  editForm = {
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  };

  // groups parsed from the permission strings
  permGroups = computed<PermGroup[]>(() => this.buildGroups(this.permissions()));

  filteredGroups = computed<PermGroup[]>(() => {
    this.searchTick(); // dependency
    const q = this.search.trim().toLowerCase();
    if (!q) return this.permGroups();
    return this.permGroups()
      .map((g) => {
        const matchHeader =
          g.label.toLowerCase().includes(q) ||
          g.resource.toLowerCase().includes(q) ||
          g.system.toLowerCase().includes(q);
        const actions = matchHeader
          ? g.actions
          : g.actions.filter(
              (a) => a.verb.toLowerCase().includes(q) || a.full.toLowerCase().includes(q),
            );
        if (actions.length === 0 && !matchHeader) return null;
        return { ...g, actions, expanded: true } as PermGroup;
      })
      .filter((g): g is PermGroup => g !== null);
  });

  systemsCount = computed(() => {
    const set = new Set<string>();
    for (const p of this.permissions()) {
      const colonIdx = p.indexOf(':');
      if (colonIdx > 0) set.add(p.substring(0, colonIdx));
    }
    return set.size;
  });

  ngOnInit() {
    this.loadProfile();
    this.loadPermissions();
  }

  // ---- permission string parsing ----
  private buildGroups(perms: string[]): PermGroup[] {
    const map = new Map<string, PermGroup>();
    for (const raw of perms) {
      if (!raw) continue;
      const { system, resource, verb, full } = this.parsePerm(raw);
      const key = `${system}::${resource}`;
      let g = map.get(key);
      if (!g) {
        g = {
          system,
          resource,
          label: this.humanizeResource(resource),
          icon: RESOURCE_ICON[resource] || 'pi pi-folder',
          actions: [],
          expanded: false,
        };
        map.set(key, g);
      }
      g.actions.push({ full, verb: verb || '·', tone: VERB_TONE[verb.toLowerCase()] || 'tone-neutral' });
    }
    // sort actions inside each group
    const order = ['read', 'write', 'update', 'delete', 'approve', 'reject', 'pre-approve', 'pre-reject',
                   'accept', 'invoice', 'transfer', 'deposit', 'withdraw', 'collect', 'send', 'send-bulk'];
    for (const g of map.values()) {
      g.actions.sort((a, b) => {
        const ai = order.indexOf(a.verb);
        const bi = order.indexOf(b.verb);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return a.verb.localeCompare(b.verb);
      });
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.system !== b.system) return a.system.localeCompare(b.system);
      return a.resource.localeCompare(b.resource);
    });
  }

  private parsePerm(raw: string) {
    // Expected: "systemKey:resource.verb"  e.g. "core:users.read"
    // Fallbacks for "resource.verb", "verb", or "systemKey:code"
    let system = '';
    let body = raw;
    const colonIdx = raw.indexOf(':');
    if (colonIdx > 0) {
      system = raw.substring(0, colonIdx);
      body = raw.substring(colonIdx + 1);
    }
    const dotIdx = body.indexOf('.');
    const resource = dotIdx === -1 ? body : body.substring(0, dotIdx);
    const verb = dotIdx === -1 ? '' : body.substring(dotIdx + 1);
    return { system: system || '—', resource: resource || '—', verb, full: raw };
  }

  private humanizeResource(resource: string): string {
    if (resource === '*') return 'كل الصلاحيات (Wildcard)';
    if (resource === 'access') return 'الوصول العام';
    return resource.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  onSearchChange() {
    this.searchTick.update((v) => v + 1);
  }

  expandAll() {
    for (const g of this.permGroups()) g.expanded = true;
    this.searchTick.update((v) => v + 1);
  }
  collapseAll() {
    for (const g of this.permGroups()) g.expanded = false;
    this.searchTick.update((v) => v + 1);
  }

  trackGroup = (_: number, g: PermGroup) => `${g.system}::${g.resource}`;

  loadProfile() {
    this.loading.set(true);
    this.accountService.getMyProfile().subscribe({
      next: (data) => {
        this.profile.set(data);
        this.editForm = {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || ''
        };
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Failed to load profile', err);
        this.ms.add({
          severity: 'error',
          summary: 'خطأ',
          detail: 'فشل تحميل معلومات الملف الشخصي'
        });
        this.loading.set(false);
      }
    });
  }

  loadPermissions() {
    this.apiService.get<string[]>('/api/permissions/me').subscribe({
      next: (perms) => {
        this.permissions.set(perms || []);
      },
      error: () => {
        const claims = this.authService.claims;
        const perms = claims?.perm || [];
        this.permissions.set(Array.isArray(perms) ? perms : []);
      }
    });
  }

  saveProfile() {
    this.saving.set(true);
    this.accountService.updateMyProfile(this.editForm).subscribe({
      next: () => {
        this.ms.add({
          severity: 'success',
          summary: 'تم',
          detail: 'تم تحديث الملف الشخصي بنجاح'
        });
        this.loadProfile();
        this.saving.set(false);
      },
      error: (err) => {
        console.error('Failed to update profile', err);
        this.ms.add({
          severity: 'error',
          summary: 'خطأ',
          detail: 'فشل تحديث الملف الشخصي'
        });
        this.saving.set(false);
      }
    });
  }
}
